import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

/**
 * CampaignAutoContent - Component for configuring and managing
 * automatic AI content generation for campaigns
 */
const CampaignAutoContent = ({ campaignId, projectId, onContentGenerated }) => {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Configuration options from API
  const [options, setOptions] = useState(null);
  
  // Current configuration
  const [config, setConfig] = useState({
    enabled: true,
    content_types: ['images'],
    style: 'professional',
    custom_style_description: '',
    image_count: 3,
    video_count: 1,
    copy_variations: 3,
    image_sizes: ['1024x1024', '1200x628'],
    video_duration: 'short',
    tone_of_voice: 'professional',
    target_platforms: ['facebook', 'instagram'],
    generate_ab_variations: true,
    ab_variation_count: 2,
    require_preview_approval: true,
    auto_publish: false
  });
  
  // Generation status
  const [generationStatus, setGenerationStatus] = useState(null);
  
  // Preview content
  const [previewContent, setPreviewContent] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Brief
  const [brief, setBrief] = useState(null);
  const [showBrief, setShowBrief] = useState(false);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('config');

  // Fetch options and current config
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch available options
        const optionsRes = await api.get('/campaigns/auto-content/options');
        setOptions(optionsRes.data);
        
        // Fetch current config if campaign exists
        if (campaignId) {
          try {
            const configRes = await api.get(`/campaigns/${campaignId}/auto-content/config`);
            if (configRes.data.config) {
              setConfig(configRes.data.config);
            }
          } catch (err) {
            // Config might not exist yet, that's ok
          }
        }
        
      } catch (err) {
        console.error('Error fetching auto-content data:', err);
        setError('Failed to load auto-content options');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [campaignId]);

  // Poll for generation status
  useEffect(() => {
    let interval;
    
    if (generationStatus?.status === 'processing' || generationStatus?.status === 'initiated') {
      interval = setInterval(async () => {
        try {
          const res = await api.get(`/campaigns/${campaignId}/auto-content/status`);
          setGenerationStatus(res.data);
          
          if (res.data.status === 'completed' || res.data.status === 'failed') {
            clearInterval(interval);
            if (res.data.status === 'completed' && onContentGenerated) {
              onContentGenerated(res.data.generated_content);
            }
          }
        } catch (err) {
          console.error('Error polling status:', err);
        }
      }, 3000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [generationStatus, campaignId, onContentGenerated]);

  const handleConfigChange = useCallback((field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleArrayToggle = useCallback((field, value) => {
    setConfig(prev => {
      const current = prev[field] || [];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  }, []);

  const saveConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      
      await api.put(`/campaigns/${campaignId}/auto-content/config`, config);
      
      setSuccess('Configuration saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving config:', err);
      setError(err.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const triggerGeneration = async () => {
    try {
      setGenerating(true);
      setError(null);
      
      const res = await api.post(`/campaigns/${campaignId}/auto-content/generate`, config);
      
      setGenerationStatus({
        status: 'initiated',
        job_id: res.data.job_id,
        content_summary: res.data.content_summary
      });
      
      setSuccess('Content generation started!');
      setTimeout(() => setSuccess(null), 3000);
      setActiveTab('status');
      
    } catch (err) {
      console.error('Error triggering generation:', err);
      setError(err.response?.data?.error || 'Failed to start content generation');
    } finally {
      setGenerating(false);
    }
  };

  const fetchPreview = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/campaigns/${campaignId}/auto-content/preview`);
      setPreviewContent(res.data.content);
      setShowPreview(true);
    } catch (err) {
      console.error('Error fetching preview:', err);
      setError(err.response?.data?.error || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrief = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/campaigns/${campaignId}/auto-content/brief`);
      setBrief(res.data);
      setShowBrief(true);
    } catch (err) {
      console.error('Error fetching brief:', err);
      setError(err.response?.data?.error || 'Failed to load brief');
    } finally {
      setLoading(false);
    }
  };

  const enhanceBrief = async () => {
    try {
      setLoading(true);
      const res = await api.post(`/campaigns/${campaignId}/auto-content/brief/enhance`);
      setBrief(res.data);
      setSuccess('Brief enhanced with AI insights!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error enhancing brief:', err);
      setError(err.response?.data?.error || 'Failed to enhance brief');
    } finally {
      setLoading(false);
    }
  };

  const approveContent = async (contentIds) => {
    try {
      await api.post(`/campaigns/${campaignId}/auto-content/approve`, {
        content_ids: contentIds
      });
      setSuccess('Content approved!');
      setTimeout(() => setSuccess(null), 3000);
      fetchPreview();
    } catch (err) {
      console.error('Error approving content:', err);
      setError(err.response?.data?.error || 'Failed to approve content');
    }
  };

  const rejectContent = async (contentIds, reason) => {
    try {
      await api.post(`/campaigns/${campaignId}/auto-content/reject`, {
        content_ids: contentIds,
        reason: reason || 'Needs revision'
      });
      setSuccess('Content rejected. You can regenerate with feedback.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error rejecting content:', err);
      setError(err.response?.data?.error || 'Failed to reject content');
    }
  };

  const regenerateContent = async (contentType, feedback) => {
    try {
      setGenerating(true);
      const res = await api.post(`/campaigns/${campaignId}/auto-content/regenerate`, {
        content_type: contentType,
        feedback: feedback,
        count: 2
      });
      setSuccess('Content regeneration started!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error regenerating content:', err);
      setError(err.response?.data?.error || 'Failed to regenerate content');
    } finally {
      setGenerating(false);
    }
  };

  // Styles
  const styles = {
    container: {
      backgroundColor: isDark ? '#1f2937' : 'white',
      borderRadius: '12px',
      boxShadow: isDark ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      border: isDark ? '1px solid #374151' : 'none'
    },
    header: {
      background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
      color: 'white',
      padding: '1.5rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    headerTitle: {
      margin: 0,
      fontSize: '1.25rem',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    tabs: {
      display: 'flex',
      borderBottom: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
      backgroundColor: isDark ? '#111827' : '#f9fafb'
    },
    tab: {
      padding: '1rem 1.5rem',
      cursor: 'pointer',
      border: 'none',
      backgroundColor: 'transparent',
      color: isDark ? '#9ca3af' : '#6b7280',
      fontWeight: '500',
      borderBottom: '2px solid transparent',
      transition: 'all 0.2s'
    },
    activeTab: {
      color: '#7C3AED',
      borderBottomColor: '#7C3AED',
      backgroundColor: isDark ? '#1f2937' : 'white'
    },
    content: {
      padding: '1.5rem'
    },
    section: {
      marginBottom: '1.5rem'
    },
    sectionTitle: {
      fontSize: '0.875rem',
      fontWeight: '600',
      color: isDark ? '#e5e7eb' : '#374151',
      marginBottom: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    toggleGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      gap: '0.5rem'
    },
    toggleButton: {
      padding: '0.75rem 0.5rem',
      borderRadius: '8px',
      border: `2px solid ${isDark ? '#374151' : '#e5e7eb'}`,
      backgroundColor: isDark ? '#1f2937' : 'white',
      color: isDark ? '#e5e7eb' : '#374151',
      cursor: 'pointer',
      textAlign: 'center',
      transition: 'all 0.2s',
      fontSize: '0.813rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0
    },
    toggleButtonActive: {
      borderColor: '#7C3AED',
      backgroundColor: isDark ? '#4c1d95' : '#f5f3ff',
      color: isDark ? '#e9d5ff' : '#7C3AED'
    },
    inputGroup: {
      marginBottom: '1rem'
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: isDark ? '#e5e7eb' : '#374151',
      marginBottom: '0.5rem'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      borderRadius: '8px',
      border: `1px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
      fontSize: '0.875rem',
      transition: 'border-color 0.2s',
      backgroundColor: isDark ? '#374151' : 'white',
      color: isDark ? '#f9fafb' : 'inherit'
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      borderRadius: '8px',
      border: `1px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
      fontSize: '0.875rem',
      backgroundColor: isDark ? '#374151' : 'white',
      color: isDark ? '#f9fafb' : 'inherit'
    },
    rangeContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    },
    range: {
      flex: 1,
      height: '6px',
      appearance: 'none',
      backgroundColor: isDark ? '#374151' : '#e5e7eb',
      borderRadius: '3px',
      outline: 'none'
    },
    rangeValue: {
      minWidth: '40px',
      textAlign: 'center',
      fontWeight: '600',
      color: '#7C3AED'
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem',
      backgroundColor: isDark ? '#374151' : '#f9fafb',
      borderRadius: '8px',
      cursor: 'pointer'
    },
    checkboxInput: {
      width: '18px',
      height: '18px',
      accentColor: '#7C3AED'
    },
    button: {
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      border: 'none',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s'
    },
    primaryButton: {
      backgroundColor: '#7C3AED',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: isDark ? '#374151' : '#f3f4f6',
      color: isDark ? '#e5e7eb' : '#374151'
    },
    alert: {
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    errorAlert: {
      backgroundColor: isDark ? '#450a0a' : '#fef2f2',
      color: isDark ? '#fca5a5' : '#991b1b',
      border: `1px solid ${isDark ? '#7f1d1d' : '#fecaca'}`
    },
    successAlert: {
      backgroundColor: isDark ? '#052e16' : '#f0fdf4',
      color: isDark ? '#86efac' : '#166534',
      border: `1px solid ${isDark ? '#166534' : '#bbf7d0'}`
    },
    statusCard: {
      padding: '1.5rem',
      backgroundColor: isDark ? '#374151' : '#f9fafb',
      borderRadius: '12px',
      textAlign: 'center',
      color: isDark ? '#e5e7eb' : 'inherit'
    },
    progressBar: {
      height: '8px',
      backgroundColor: isDark ? '#1f2937' : '#e5e7eb',
      borderRadius: '4px',
      overflow: 'hidden',
      marginTop: '1rem'
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#7C3AED',
      transition: 'width 0.3s ease'
    },
    previewCard: {
      border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
      borderRadius: '8px',
      overflow: 'hidden',
      marginBottom: '1rem'
    },
    previewHeader: {
      backgroundColor: isDark ? '#374151' : '#f9fafb',
      padding: '0.75rem 1rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`,
      color: isDark ? '#e5e7eb' : 'inherit'
    },
    previewContent: {
      padding: '1rem',
      backgroundColor: isDark ? '#1f2937' : 'white',
      color: isDark ? '#e5e7eb' : 'inherit'
    },
    briefSection: {
      backgroundColor: isDark ? '#374151' : '#f9fafb',
      padding: '1rem',
      borderRadius: '8px',
      marginBottom: '1rem',
      color: isDark ? '#e5e7eb' : 'inherit'
    }
  };

  if (loading && !options) {
    return (
      <div style={styles.container}>
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <div className="spinner"></div>
          <p style={{ marginTop: '1rem', color: isDark ? '#9ca3af' : '#6b7280' }}>Loading auto-content options...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h3 style={styles.headerTitle}>
          <span>🎨</span> Auto-Generate Content
        </h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <span style={{ fontSize: '0.875rem' }}>Enable</span>
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => handleConfigChange('enabled', e.target.checked)}
            style={{ width: '40px', height: '20px', accentColor: 'white' }}
          />
        </label>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'config' ? styles.activeTab : {}) }}
          onClick={() => setActiveTab('config')}
        >
          ⚙️ Configuration
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'status' ? styles.activeTab : {}) }}
          onClick={() => setActiveTab('status')}
        >
          📊 Status
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'preview' ? styles.activeTab : {}) }}
          onClick={() => { setActiveTab('preview'); fetchPreview(); }}
        >
          👁️ Preview
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'brief' ? styles.activeTab : {}) }}
          onClick={() => { setActiveTab('brief'); fetchBrief(); }}
        >
          📋 Brief
        </button>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Alerts */}
        {error && (
          <div style={{ ...styles.alert, ...styles.errorAlert }}>
            <span>⚠️</span> {error}
          </div>
        )}
        {success && (
          <div style={{ ...styles.alert, ...styles.successAlert }}>
            <span>✓</span> {success}
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === 'config' && config.enabled && (
          <>
            {/* Content Types */}
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Content Types to Generate</h4>
              <div style={styles.toggleGrid}>
                {options?.content_types?.map(ct => (
                  <button
                    key={ct.value}
                    style={{
                      ...styles.toggleButton,
                      ...(config.content_types?.includes(ct.value) ? styles.toggleButtonActive : {})
                    }}
                    onClick={() => handleArrayToggle('content_types', ct.value)}
                  >
                    {ct.value === 'images' && '🖼️ '}
                    {ct.value === 'videos' && '🎬 '}
                    {ct.value === 'copy' && '✍️ '}
                    {ct.value === 'social_posts' && '📱 '}
                    {ct.value === 'all' && '🎯 '}
                    {ct.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Style Selection */}
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Visual Style</h4>
              <div style={styles.toggleGrid}>
                {options?.styles?.map(st => (
                  <button
                    key={st.value}
                    style={{
                      ...styles.toggleButton,
                      ...(config.style === st.value ? styles.toggleButtonActive : {})
                    }}
                    onClick={() => handleConfigChange('style', st.value)}
                  >
                    {st.label}
                  </button>
                ))}
              </div>
              {config.style === 'custom' && (
                <div style={{ marginTop: '1rem' }}>
                  <textarea
                    placeholder="Describe your custom style..."
                    value={config.custom_style_description}
                    onChange={(e) => handleConfigChange('custom_style_description', e.target.value)}
                    style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
                  />
                </div>
              )}
            </div>

            {/* Quantity Settings */}
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Quantity Settings</h4>
              
              {config.content_types?.includes('images') && (
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Number of Images: {config.image_count}</label>
                  <div style={styles.rangeContainer}>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={config.image_count}
                      onChange={(e) => handleConfigChange('image_count', parseInt(e.target.value))}
                      style={styles.range}
                    />
                    <span style={styles.rangeValue}>{config.image_count}</span>
                  </div>
                </div>
              )}

              {config.content_types?.includes('videos') && (
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Number of Videos: {config.video_count}</label>
                  <div style={styles.rangeContainer}>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={config.video_count}
                      onChange={(e) => handleConfigChange('video_count', parseInt(e.target.value))}
                      style={styles.range}
                    />
                    <span style={styles.rangeValue}>{config.video_count}</span>
                  </div>
                </div>
              )}

              {(config.content_types?.includes('copy') || config.content_types?.includes('all')) && (
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Copy Variations: {config.copy_variations}</label>
                  <div style={styles.rangeContainer}>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={config.copy_variations}
                      onChange={(e) => handleConfigChange('copy_variations', parseInt(e.target.value))}
                      style={styles.range}
                    />
                    <span style={styles.rangeValue}>{config.copy_variations}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Image Sizes */}
            {config.content_types?.includes('images') && (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>Image Sizes</h4>
                <div style={styles.toggleGrid}>
                  {options?.image_sizes?.map(size => (
                    <button
                      key={size.value}
                      style={{
                        ...styles.toggleButton,
                        ...(config.image_sizes?.includes(size.value) ? styles.toggleButtonActive : {}),
                        whiteSpace: 'normal',
                        lineHeight: '1.3'
                      }}
                      onClick={() => handleArrayToggle('image_sizes', size.value)}
                    >
                      <div style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis' }}>{size.label}</div>
                      <div style={{ fontSize: '0.7rem', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '2px' }}>{size.aspect}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Target Platforms */}
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Target Platforms</h4>
              <div style={styles.toggleGrid}>
                {options?.platforms?.map(platform => (
                  <button
                    key={platform.value}
                    style={{
                      ...styles.toggleButton,
                      ...(config.target_platforms?.includes(platform.value) ? styles.toggleButtonActive : {})
                    }}
                    onClick={() => handleArrayToggle('target_platforms', platform.value)}
                  >
                    {platform.value === 'facebook' && '📘 '}
                    {platform.value === 'instagram' && '📸 '}
                    {platform.value === 'twitter' && '🐦 '}
                    {platform.value === 'linkedin' && '💼 '}
                    {platform.value === 'tiktok' && '🎵 '}
                    {platform.value === 'youtube' && '▶️ '}
                    {platform.value === 'email' && '📧 '}
                    {platform.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tone of Voice */}
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Tone of Voice</h4>
              <select
                value={config.tone_of_voice}
                onChange={(e) => handleConfigChange('tone_of_voice', e.target.value)}
                style={styles.select}
              >
                {options?.tone_options?.map(tone => (
                  <option key={tone.value} value={tone.value}>{tone.label}</option>
                ))}
              </select>
            </div>

            {/* A/B Testing */}
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>A/B Testing</h4>
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={config.generate_ab_variations}
                  onChange={(e) => handleConfigChange('generate_ab_variations', e.target.checked)}
                  style={styles.checkboxInput}
                />
                <div>
                  <div style={{ fontWeight: '500' }}>Generate A/B Variations</div>
                  <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                    Create multiple versions for testing
                  </div>
                </div>
              </label>
              
              {config.generate_ab_variations && (
                <div style={{ marginTop: '1rem' }}>
                  <label style={styles.label}>Number of Variations: {config.ab_variation_count}</label>
                  <div style={styles.rangeContainer}>
                    <input
                      type="range"
                      min="2"
                      max="5"
                      value={config.ab_variation_count}
                      onChange={(e) => handleConfigChange('ab_variation_count', parseInt(e.target.value))}
                      style={styles.range}
                    />
                    <span style={styles.rangeValue}>{config.ab_variation_count}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Publishing Settings */}
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Publishing Settings</h4>
              
              <label style={{ ...styles.checkbox, marginBottom: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={config.require_preview_approval}
                  onChange={(e) => handleConfigChange('require_preview_approval', e.target.checked)}
                  style={styles.checkboxInput}
                />
                <div>
                  <div style={{ fontWeight: '500' }}>Require Preview Approval</div>
                  <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                    Review content before using in campaign
                  </div>
                </div>
              </label>

              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={config.auto_publish}
                  onChange={(e) => handleConfigChange('auto_publish', e.target.checked)}
                  style={styles.checkboxInput}
                  disabled={config.require_preview_approval}
                />
                <div>
                  <div style={{ fontWeight: '500' }}>Auto-Publish to Campaign</div>
                  <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                    Automatically add approved content to campaign
                  </div>
                </div>
              </label>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <button
                onClick={saveConfig}
                disabled={saving}
                style={{ ...styles.button, ...styles.secondaryButton }}
              >
                {saving ? '💾 Saving...' : '💾 Save Configuration'}
              </button>
              
              <button
                onClick={triggerGeneration}
                disabled={generating || !campaignId}
                style={{ ...styles.button, ...styles.primaryButton, flex: 1 }}
              >
                {generating ? (
                  <>⏳ Generating...</>
                ) : (
                  <>🚀 Generate Content</>
                )}
              </button>
            </div>
          </>
        )}

        {activeTab === 'config' && !config.enabled && (
          <div style={styles.statusCard}>
            <span style={{ fontSize: '3rem' }}>🎨</span>
            <h4 style={{ margin: '1rem 0 0.5rem' }}>Auto-Content Generation Disabled</h4>
            <p style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
              Enable this feature to automatically generate images, videos, and copy for your campaign.
            </p>
          </div>
        )}

        {/* Status Tab */}
        {activeTab === 'status' && (
          <div>
            {generationStatus ? (
              <div style={styles.statusCard}>
                <div style={{ marginBottom: '1rem' }}>
                  {generationStatus.status === 'processing' && (
                    <span style={{ fontSize: '3rem' }}>⏳</span>
                  )}
                  {generationStatus.status === 'completed' && (
                    <span style={{ fontSize: '3rem' }}>✅</span>
                  )}
                  {generationStatus.status === 'failed' && (
                    <span style={{ fontSize: '3rem' }}>❌</span>
                  )}
                  {generationStatus.status === 'initiated' && (
                    <span style={{ fontSize: '3rem' }}>🚀</span>
                  )}
                </div>
                
                <h4 style={{ margin: '0 0 0.5rem' }}>
                  {generationStatus.status === 'processing' && 'Generating Content...'}
                  {generationStatus.status === 'completed' && 'Content Generated!'}
                  {generationStatus.status === 'failed' && 'Generation Failed'}
                  {generationStatus.status === 'initiated' && 'Starting Generation...'}
                </h4>
                
                {generationStatus.current_stage && (
                  <p style={{ color: isDark ? '#9ca3af' : '#6b7280', margin: '0 0 1rem' }}>
                    Current Stage: {generationStatus.current_stage}
                  </p>
                )}
                
                {generationStatus.progress !== undefined && (
                  <div style={styles.progressBar}>
                    <div
                      style={{
                        ...styles.progressFill,
                        width: `${generationStatus.progress}%`
                      }}
                    />
                  </div>
                )}
                
                {generationStatus.content_summary && (
                  <div style={{ marginTop: '1.5rem', textAlign: 'left' }}>
                    <h5 style={{ marginBottom: '0.5rem' }}>Content Summary:</h5>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      <li>🖼️ Images: {generationStatus.content_summary.images}</li>
                      <li>🎬 Videos: {generationStatus.content_summary.videos}</li>
                      <li>✍️ Copy Variations: {generationStatus.content_summary.copy_variations}</li>
                      <li>📱 Social Posts: {generationStatus.content_summary.social_posts}</li>
                      <li>🔀 A/B Variations: {generationStatus.content_summary.ab_variations}</li>
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div style={styles.statusCard}>
                <span style={{ fontSize: '3rem' }}>📭</span>
                <h4 style={{ margin: '1rem 0 0.5rem' }}>No Active Generation</h4>
                <p style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                  Configure and start content generation to see status here.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Preview Tab */}
        {activeTab === 'preview' && (
          <div>
            {previewContent ? (
              <>
                {/* Images Preview */}
                {previewContent.images?.length > 0 && (
                  <div style={styles.previewCard}>
                    <div style={styles.previewHeader}>
                      <span style={{ fontWeight: '600' }}>🖼️ Images ({previewContent.images.length})</span>
                    </div>
                    <div style={styles.previewContent}>
                      {previewContent.images.map((img, idx) => (
                        <div key={idx} style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: isDark ? '#374151' : '#f9fafb', borderRadius: '8px' }}>
                          <p><strong>Prompt:</strong> {img.prompt}</p>
                          <p><strong>Style:</strong> {img.style}</p>
                          <p><strong>Use Case:</strong> {img.use_case}</p>
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                            <button
                              onClick={() => approveContent([`img_${idx}`])}
                              style={{ ...styles.button, ...styles.primaryButton, padding: '0.5rem 1rem' }}
                            >
                              ✓ Approve
                            </button>
                            <button
                              onClick={() => rejectContent([`img_${idx}`], 'Needs revision')}
                              style={{ ...styles.button, ...styles.secondaryButton, padding: '0.5rem 1rem' }}
                            >
                              ✗ Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Copy Preview */}
                {previewContent.copy_variations?.length > 0 && (
                  <div style={styles.previewCard}>
                    <div style={styles.previewHeader}>
                      <span style={{ fontWeight: '600' }}>✍️ Copy Variations ({previewContent.copy_variations.length})</span>
                    </div>
                    <div style={styles.previewContent}>
                      {previewContent.copy_variations.map((copy, idx) => (
                        <div key={idx} style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: isDark ? '#374151' : '#f9fafb', borderRadius: '8px' }}>
                          <h5 style={{ margin: '0 0 0.5rem' }}>{copy.headline}</h5>
                          {copy.subheadline && <p style={{ color: isDark ? '#9ca3af' : '#6b7280', margin: '0 0 0.5rem' }}>{copy.subheadline}</p>}
                          <p style={{ margin: '0 0 0.5rem' }}>{copy.body}</p>
                          <p style={{ fontWeight: '600', color: '#7C3AED' }}>{copy.cta}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Social Posts Preview */}
                {previewContent.social_posts?.length > 0 && (
                  <div style={styles.previewCard}>
                    <div style={styles.previewHeader}>
                      <span style={{ fontWeight: '600' }}>📱 Social Posts ({previewContent.social_posts.length})</span>
                    </div>
                    <div style={styles.previewContent}>
                      {previewContent.social_posts.map((post, idx) => (
                        <div key={idx} style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: isDark ? '#374151' : '#f9fafb', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '1.25rem' }}>
                              {post.platform === 'instagram' && '📸'}
                              {post.platform === 'facebook' && '📘'}
                              {post.platform === 'twitter' && '🐦'}
                              {post.platform === 'linkedin' && '💼'}
                            </span>
                            <span style={{ fontWeight: '500', textTransform: 'capitalize' }}>{post.platform}</span>
                          </div>
                          <p style={{ margin: '0 0 0.5rem' }}>{post.text}</p>
                          {post.hashtags && (
                            <p style={{ color: '#7C3AED', margin: 0 }}>
                              {post.hashtags.map(tag => `#${tag}`).join(' ')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={styles.statusCard}>
                <span style={{ fontSize: '3rem' }}>👁️</span>
                <h4 style={{ margin: '1rem 0 0.5rem' }}>No Content to Preview</h4>
                <p style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                  Generate content first to preview it here.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Brief Tab */}
        {activeTab === 'brief' && (
          <div>
            {brief ? (
              <>
                <div style={styles.briefSection}>
                  <h4 style={{ margin: '0 0 1rem' }}>📋 Campaign Brief</h4>
                  <p><strong>Name:</strong> {brief.brief?.campaign_name}</p>
                  <p><strong>Type:</strong> {brief.brief?.campaign_type}</p>
                  <p><strong>Description:</strong> {brief.brief?.description}</p>
                  
                  {brief.brief?.key_messages?.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <strong>Key Messages:</strong>
                      <ul>
                        {brief.brief.key_messages.map((msg, idx) => (
                          <li key={idx}>{msg}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {brief.brief?.brand_guidelines?.visual_themes && (
                    <div style={{ marginTop: '1rem' }}>
                      <strong>Visual Themes:</strong>
                      <ul>
                        {brief.brief.brand_guidelines.visual_themes.map((theme, idx) => (
                          <li key={idx}>{theme}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={enhanceBrief}
                  disabled={loading}
                  style={{ ...styles.button, ...styles.primaryButton }}
                >
                  {loading ? '⏳ Enhancing...' : '✨ Enhance Brief with AI'}
                </button>
              </>
            ) : (
              <div style={styles.statusCard}>
                <span style={{ fontSize: '3rem' }}>📋</span>
                <h4 style={{ margin: '1rem 0 0.5rem' }}>Loading Brief...</h4>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignAutoContent;
